@if (stop(); as stop) {
<app-card
  [accentStyle]="accentStyle()"
  [editable]="true"
  [collapsible]="true"
  [(collapsed)]="collapsed"
>
  <!-- Collapsed Summary -->
  <div card-summary class="flex items-center justify-between cursor-pointer group pt-4 pb-1" (click)="collapsed.set(false)" tabindex="0" (keydown.enter)="collapsed.set(false)">
    <div class="flex flex-col gap-1 text-sm">
      <div class="flex items-center gap-2 flex-wrap">
        <lucide-icon [name]="stopIconName()" size="14" class="text-muted"></lucide-icon>
        <app-text class="truncate font-medium">{{ stop.address?.city }}, {{ stop.address?.state }}</app-text>
        <app-badge [variant]="statusInfo().color" [title]="statusInfo().label" size="xs"></app-badge>
        @if (stopTypeBadge(); as typeBadge) {
          <app-badge [variant]="typeBadge.variant" badgeStyle="outline" [title]="typeBadge.label" size="xs"></app-badge>
        }
        @if (stickyNotesCount() > 0) {
          <span
            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-surface-high border border-border text-primary"
            (click)="$event.stopPropagation(); openStickyNotes.emit()"
            title="View stop sticky notes"
          >
            <lucide-icon name="StickyNote" size="11" class="text-primary"></lucide-icon>
            <app-text renderAs="span" size="xs" weight="semibold" class="text-primary">{{ stickyNotesCount() }}</app-text>
          </span>
        }
        @if (isCompleted()) {
          <lucide-icon name="CheckCircle2" size="16" class="text-success ml-1"></lucide-icon>
        }
      </div>
      <div class="flex items-center gap-2">
        <lucide-icon name="Clock" size="14" class="text-muted"></lucide-icon>
        <app-text class="truncate text-muted">
          @if (stop?.appointment?.type === 'fcfs' && stop?.appointment?.from?.date) {
            {{ stop?.appointment?.from?.date | date : "MMM d" : 'UTC' }} &middot; {{ stop?.appointment?.from?.time | formatTime }}
          } @else if (stop?.appointment?.type === 'strict' && stop?.appointment?.from?.date) {
            {{ stop?.appointment?.from?.date | date : "MMM d" : 'UTC' }} &middot; {{ stop?.appointment?.from?.time | formatTime }}
          } @else {
            Flexible / Open Window
          }
        </app-text>
      </div>
    </div>
    <div class="flex items-center text-muted group-hover:text-foreground transition-colors">
      <lucide-icon name="ChevronDown" size="20"></lucide-icon>
    </div>
  </div>
  <div class="flex flex-col gap-1 pt-2">
    <div class="flex gap-2">
      <div
        class="flex items-center justify-center rounded-full h-6 w-6 p-1 shrink-0"
        [class.bg-secondary-dark]="
          stop.stopType !== 'pickup' &&
          stop.stopType !== 'delivery' &&
          stop.stopType !== 'both' &&
          stop.stopType !== 'handoff'
        "
        [class.bg-info]="stop.stopType === 'pickup'"
        [class.bg-success]="stop.stopType === 'delivery'"
        [class.bg-accent]="stop.stopType === 'both'"
        [class.bg-warning]="stop.stopType === 'handoff'"
      >
        <lucide-icon
          [name]="stopIconName()"
          size="16"
          class="z-10 text-primary-foreground"
        ></lucide-icon>
      </div>
      <!-- Stop Details -->
      <div class="pb-2 grow flex flex-col gap-1 min-w-0">
        <div class="flex gap-2 items-center flex-wrap">
          <app-text weight="medium" color="muted">{{ stopLabel() }}</app-text>
          <app-badge [variant]="statusInfo().color" [title]="statusInfo().label" size="xs"></app-badge>
          @if (stopTypeBadge(); as typeBadge) {
            <app-badge [variant]="typeBadge.variant" badgeStyle="outline" [title]="typeBadge.label" size="xs"></app-badge>
          }
          @if (servicedByLabel(); as servicedLabel) {
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-border">
              <lucide-icon name="UserCheck" size="11" class="text-primary"></lucide-icon>
              <app-text renderAs="span" size="xs" weight="medium" class="text-foreground">{{ servicedLabel }}</app-text>
            </span>
          }
          @if (isCompleted()) {
            <lucide-icon name="CheckCircle2" size="16" class="text-success"></lucide-icon>
          }
        </div>
        <app-text weight="semibold" class="truncate">{{ stop.companyName || (stop.stopType === 'handoff' ? 'Relay Handoff Location' : 'Facility') }}</app-text>
      </div>
      <div class="ml-auto flex items-center gap-1 shrink-0">
        @if (canSplitHere()) {
        <app-button
          variant="ghost"
          size="xs"
          icon="Split"
          title="Split route / hand off from this stop"
          (click)="splitHere.emit()"
        >
          Relay
        </app-button>
        }
        @if (stickyNotesCount() > 0) {
        <app-button
          variant="secondary"
          size="xs"
          shape="pill"
          icon="StickyNote"
          title="View or add sticky notes for this stop"
          (click)="openStickyNotes.emit()"
        >
          {{ stickyNotesCount() }}
        </app-button>
        } @else {
        <app-button
          variant="ghost"
          size="sm"
          [iconOnly]="true"
          iconColor="text-muted"
          icon="StickyNote"
          title="Add sticky note for this stop"
          (click)="openStickyNotes.emit()"
        />
        }
        @if (canEdit()) {
        <app-button
          variant="ghost"
          size="sm"
          [iconOnly]="true"
          iconColor="text-muted"
          icon="Pencil"
          (click)="edit.emit()"
        />
        }
        <app-button
          variant="ghost"
          size="sm"
          [iconOnly]="true"
          iconColor="text-muted"
          icon="ChevronUp"
          (click)="collapsed.set(true)"
        />
      </div>
    </div>
    <!-- Address -->
    <div class="flex items-center gap-2 text-sm text-muted">
      <lucide-icon name="MapPin" size="15"></lucide-icon>
      <a
        [href]="convertAddressToGoogleUrl(stop.address)"
        target="_blank"
        rel="noopener noreferrer"
        class="underline hover:text-primary transition"
      >
        <app-text size="sm" color="muted">
          {{ stop.address?.street }}, {{ stop.address?.street2 || "" }}
          {{ stop.address?.street2 ? "," : "" }} {{ stop.address?.city }},
          {{ stop.address?.state }}, {{ stop.address?.zip }},
          {{ stop.address?.country }}
        </app-text>
      </a>
    </div>
    <!-- Line 2: Appointment Details -->
    <div class="flex gap-2 text-sm text-muted">
      <lucide-icon name="Clock" size="15"></lucide-icon>
      @if (!stop?.appointment || !stop?.appointment?.from?.date) {
      <app-text size="sm" color="muted">Flexible / Open Window</app-text>
      }
      <!-- Strict Appointment -->
      @else if (stop?.appointment?.type === 'strict') {
      <app-badge [title]="stop?.appointment?.type || ''" size="xs" class="self-start font-bold uppercase"></app-badge>
      <div class="flex items-center gap-2">
        <span class="font-semibold text-text">{{
          stop?.appointment?.from?.date | date : "EEE, MMM d" : 'UTC'
        }}</span>
        <div class="w-1 h-1 rounded-full bg-muted"></div>
        <span class="font-semibold text-text">{{
          stop?.appointment?.from?.time | formatTime
        }}</span>
      </div>
      }
      <!-- FCFS Appointment -->
      @else if (stop?.appointment?.type === 'fcfs') {
      <app-badge [title]="stop?.appointment?.type || ''" size="xs" class="self-start font-bold uppercase"></app-badge>
      <div class="flex items-center gap-2 flex-wrap">
        <span class="font-semibold text-text"
          >{{ stop?.appointment?.from?.date | date : "MMM d" : 'UTC' }} &middot;
          {{ stop?.appointment?.from?.time | formatTime }}</span
        >
        <lucide-icon name="ArrowRight" size="12"></lucide-icon>
        <span class="font-semibold text-text"
          >{{ stop?.appointment?.to?.date | date : "MMM d" : 'UTC' }} &middot;
          {{ stop?.appointment?.to?.time | formatTime }}</span
        >
      </div>
      }
    </div>

    <!-- Relay Drop / Hook Coordination Status -->
    @if (hasRelayTracking()) {
    <div class="mt-3 p-2.5 rounded-lg bg-surface border border-border flex flex-col gap-2" animate.enter="hv-fade-up-sm">
      <div class="flex items-center gap-1.5">
        <lucide-icon name="ArrowRightLeft" size="14" class="text-warning shrink-0"></lucide-icon>
        <app-text size="xs" weight="semibold" class="text-foreground">Relay Exchange Progress</app-text>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div class="flex items-center justify-between px-2.5 py-1.5 rounded bg-surface-high border border-border">
          <div class="flex items-center gap-1.5 min-w-0">
            <lucide-icon [name]="outgoingDropDone() ? 'CheckCircle2' : 'Clock'" size="13" [class]="outgoingDropDone() ? 'text-success shrink-0' : 'text-muted shrink-0'"></lucide-icon>
            <app-text size="xs" weight="medium" class="truncate">Outgoing Drop</app-text>
          </div>
          <app-text size="xs" [color]="outgoingDropDone() ? 'default' : 'muted'" weight="semibold">
            {{ outgoingDropDone() ? (outgoingDroppedDisplay() || 'Done') : 'Pending' }}
          </app-text>
        </div>
        <div class="flex items-center justify-between px-2.5 py-1.5 rounded bg-surface-high border border-border">
          <div class="flex items-center gap-1.5 min-w-0">
            <lucide-icon [name]="oncomingHookDone() ? 'CheckCircle2' : 'Clock'" size="13" [class]="oncomingHookDone() ? 'text-success shrink-0' : 'text-muted shrink-0'"></lucide-icon>
            <app-text size="xs" weight="medium" class="truncate">Oncoming Hook</app-text>
          </div>
          <app-text size="xs" [color]="oncomingHookDone() ? 'default' : 'muted'" weight="semibold">
            {{ oncomingHookDone() ? (oncomingHookedDisplay() || 'Done') : 'Pending' }}
          </app-text>
        </div>
      </div>
      @if (stop.relay?.dropSpotOrNotes || stop.relay?.sealNumber) {
        <div class="flex flex-wrap gap-3 pt-1 text-xs">
          @if (stop.relay?.dropSpotOrNotes) {
            <app-text size="xs" color="muted">Spot: {{ stop.relay?.dropSpotOrNotes }}</app-text>
          }
          @if (stop.relay?.sealNumber) {
            <app-text size="xs" color="muted">Seal: {{ stop.relay?.sealNumber }}</app-text>
          }
        </div>
      }
    </div>
    }

    <!-- Progress / Timeline -->
    @if (etaDisplay() || arrivedDisplay() || checkInDisplay() || stop.dockNumber || checkOutDisplay() || departedDisplay()) {
    <div class="mt-4 border-t border-border pt-3" animate.enter="hv-fade-up-sm">
      <app-text size="sm" weight="semibold" class="mb-2 block">Progress</app-text>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        @if (etaDisplay(); as eta) {
        <div>
          <app-text size="xs" color="muted">ETA</app-text>
          <app-text size="sm" weight="medium">{{ eta }}</app-text>
        </div>
        }
        @if (arrivedDisplay(); as arr) {
        <div>
          <app-text size="xs" color="muted">Gate Arrival</app-text>
          <app-text size="sm" weight="medium">{{ arr }}</app-text>
        </div>
        }
        @if (checkInDisplay(); as ci) {
        <div>
          <app-text size="xs" color="muted">Check-In</app-text>
          <app-text size="sm" weight="medium">{{ ci }}</app-text>
        </div>
        }
        @if (stop.dockNumber) {
        <div>
          <app-text size="xs" color="muted">Dock</app-text>
          <app-text size="sm" weight="medium" class="uppercase">
            {{ stop.dockNumber }}
            @if (dockedDisplay(); as dd) {
              <span class="text-xs text-muted font-normal block">{{ dd }}</span>
            }
          </app-text>
        </div>
        }
        @if (checkOutDisplay(); as co) {
        <div>
          <app-text size="xs" color="muted">Check-Out</app-text>
          <app-text size="sm" weight="medium">{{ co }}</app-text>
        </div>
        }
        @if (departedDisplay(); as dep) {
        <div>
          <app-text size="xs" color="muted">Departed</app-text>
          <app-text size="sm" weight="medium">{{ dep }}</app-text>
        </div>
        }
      </div>
    </div>
    }

    <!-- Weight -->
    <div class="flex items-center gap-2 text-sm text-muted mt-4 border-t border-border pt-3">
      <lucide-icon name="weight" size="15"></lucide-icon>
      <app-text size="sm" color="muted"
        >{{ stop?.weight || "--" }} lbs</app-text
      >
    </div>

    <!-- Reference Numbers -->
    @if (stop.referenceNumbers && stop.referenceNumbers.length > 0) {
    <div
      class="mt-4 grid grid-cols-3 gap-3 text-left border-t border-border pt-3"
    >
      @for (refNum of stop.referenceNumbers; track $index) {
      <div class="min-w-0">
        <app-text size="xs" color="muted" class="truncate">{{
          refNum.key | enumLabel : "ReferenceType"
        }}</app-text>
        <app-text size="xs" weight="medium" class="uppercase break-all">{{
          refNum.value
        }}</app-text>
      </div>
      }
    </div>
    }
    <!-- Items count -->
    @if (stop.itemCounts && stop.itemCounts.length > 0) {
    <div
      class="mt-4 grid grid-cols-3 gap-3 text-center border-t border-border pt-3"
    >
      @for (itemCount of stop.itemCounts; track $index) {
      <div class="min-w-0">
        <app-text size="xs" color="muted" class="break-words">{{
          itemCount.key | enumLabel : "ItemType"
        }}</app-text>
        <app-text size="xs" weight="medium" class="uppercase break-all">{{
          itemCount.value
        }}</app-text>
      </div>
      }
    </div>
    }

    <!-- Instructions & Notes -->
    @if (stop.instructions || stop.notes) {
    <div class="text-sm border-t border-border pt-3 mt-4">
      <div class="flex items-center justify-between">
        <app-text size="sm" weight="semibold">Instructions & Notes</app-text>
        <app-button variant="ghost" size="xs" (click)="notesCollapsed.set(!notesCollapsed())">
          <span class="inline-flex items-center gap-1">
            <span>{{ notesCollapsed() ? 'Show Notes' : 'Hide Notes' }}</span>
            <lucide-icon
              name="ChevronDown"
              size="14"
              class="transition-transform duration-200"
              [class.rotate-180]="!notesCollapsed()"
            ></lucide-icon>
          </span>
        </app-button>
      </div>
      <div class="hv-collapse-grid" [class.is-open]="!notesCollapsed()">
        <div class="hv-collapse-inner">
          <div class="space-y-3 pt-3">
            @if (stop.instructions) {
            <div>
              <app-text size="xs" color="muted">Driver Instructions</app-text>
              <app-text weight="medium">{{ stop.instructions }}</app-text>
            </div>
            } @if (stop.notes) {
            <div>
              <app-text size="xs" color="muted">Other Notes</app-text>
              <app-text weight="medium">{{ stop.notes }}</app-text>
            </div>
            }
          </div>
        </div>
      </div>
    </div>
    }
  </div>
  <!-- Add document section if any documents exist -->
  @if (hasStopDocuments()) {
  <div class="mt-3 flex flex-wrap gap-1 border-t border-border pt-2" animate.enter="hv-fade-up-sm">
    @if (getStopDocUrl(stop.documents?.billOfLading); as bolUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="FileText"
      (click)="
        viewStopDocument(
          'billOfLading',
          bolUrl,
          'BOL',
          'pdf'
        )
      "
    >
      BOL
    </app-button>
    } @if (getStopDocUrl(stop.documents?.proofOfDelivery); as podUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="FileCheck"
      (click)="
        viewStopDocument(
          'proofOfDelivery',
          podUrl,
          'POD',
          'pdf'
        )
      "
    >
      POD
    </app-button>
    } @if (getStopDocUrl(stop.documents?.loadImage); as loadImgUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="Image"
      (click)="
        viewStopDocument(
          'loadImage',
          loadImgUrl,
          'Load Photo',
          'image'
        )
      "
    >
      Load Photo
    </app-button>
    } @if (getStopDocUrl(stop.documents?.sealImage); as sealImgUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="Image"
      (click)="
        viewStopDocument(
          'sealImage',
          sealImgUrl,
          'Seal Photo',
          'image'
        )
      "
    >
      Seal Photo
    </app-button>
    } @if (getStopDocUrl(stop.documents?.temperatureImage); as tempImgUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="Thermometer"
      (click)="
        viewStopDocument(
          'temperatureImage',
          tempImgUrl,
          'Temperature',
          'image'
        )
      "
    >
      Temperature
    </app-button>
    } @if (getStopDocUrl(stop.documents?.lumperReceipt); as lumperRecUrl) {
    <app-button
      variant="ghost"
      size="xs"
      prefixIcon="Receipt"
      (click)="
        viewStopDocument(
          'lumperReceipt',
          lumperRecUrl,
          'Lumper Receipt',
          'pdf'
        )
      "
    >
      Lumper Receipt
    </app-button>
    }
  </div>
  }
</app-card>
}
